import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Icon } from '@iconify/react';
import CONFIG from '../../config';
import LoadingSpinner from '../common/LoadingSpinner';

const BatchStats = ({ project }) => {
    const [stats, setStats] = useState({
        total_in_queue: 0,
        total_running: 0,
        total_completed: 0,
        total_rejected: 0,
        batches: {}
    });
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!project) return;
        fetchStats();
        const interval = setInterval(fetchStats, 8000);
        return () => clearInterval(interval);
    }, [project]);

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/candidates/batch-stats/${project._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(resp.data);
        } catch (err) {
            console.error('Failed to fetch batch stats:', err);
        } finally {
            setLoading(false);
        }
    };

    const cards = [
        { label: 'In Track Queue', value: stats.total_in_queue, color: '#0ea5e9', icon: 'ion:people-outline' },
        { label: 'Actively Running', value: stats.total_running ?? 0, color: '#10b981', icon: 'ion:timer-outline' },
        { label: 'Run Completed', value: stats.total_completed ?? 0, color: 'var(--primary)', icon: 'ion:checkmark-done-circle-outline' },
        { label: 'Rejected / Failed', value: stats.total_rejected ?? 0, color: '#ef4444', icon: 'ion:close-circle-outline' },
    ];

    const [expandedBatchId, setExpandedBatchId] = useState(null);
    const [batchCandidates, setBatchCandidates] = useState([]);
    const [fetchingBatch, setFetchingBatch] = useState(false);

    const toggleBatchView = async (batchId) => {
        if (expandedBatchId === batchId) {
            setExpandedBatchId(null);
            return;
        }

        setExpandedBatchId(batchId);
        setFetchingBatch(true);
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/candidates/batch/${batchId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBatchCandidates(resp.data);
        } catch (err) {
            console.error('Failed to fetch batch candidates:', err);
        } finally {
            setFetchingBatch(false);
        }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {/* Header */}
            <div>
                <h2 style={{ fontSize: '26px', fontWeight: 900, margin: 0 }}>Batch Statistics</h2>
                <p style={{ color: 'var(--text-muted)', marginTop: '4px', fontWeight: 500 }}>
                    Real-time batch and candidate stage breakdown for <strong>{project?.name}</strong>
                </p>
            </div>

            {/* KPI Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
                {cards.map(card => (
                    <div key={card.label} className="ro-stat-card" style={{ textAlign: 'center' }}>
                        <div style={{ width: '56px', height: '56px', borderRadius: '16px', background: `${card.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}>
                            <Icon icon={card.icon} width="28" height="28" color={card.color} />
                        </div>
                        <div style={{ fontSize: '40px', fontWeight: 900, color: card.color }}>
                            {loading ? '—' : card.value}
                        </div>
                        <div className="ro-stat-label" style={{ marginTop: '6px' }}>{card.label}</div>
                    </div>
                ))}
            </div>

            {/* Batch Breakdown Table */}
            <div className="ro-table-container">
                <div style={{ padding: '20px 24px', background: '#f8fafc', borderBottom: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontWeight: 900, fontSize: '16px' }}>Batch Breakdown</h3>
                    <button
                        onClick={fetchStats}
                        style={{ background: 'none', border: '1px solid var(--border-light)', borderRadius: '8px', padding: '6px 14px', cursor: 'pointer', fontSize: '12px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}
                    >
                        <Icon icon="ion:refresh" width="14" /> Refresh
                    </button>
                </div>
                <div style={{ overflowY: 'auto' }}>
                    <table className="ro-table" style={{ width: '100%' }}>
                        <thead>
                            <tr>
                                <th>Batch ID</th>
                                <th>Candidates</th>
                                <th>Status</th>
                                <th style={{ textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(stats.batches).length === 0 ? (
                                <tr>
                                    <td colSpan="4" style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                                        No batch data available yet. Use the Simulator to add candidates.
                                    </td>
                                </tr>
                            ) : (
                                Object.entries(stats.batches).map(([batchId, count]) => (
                                    <React.Fragment key={batchId}>
                                        <tr style={{ cursor: 'pointer', background: expandedBatchId === batchId ? 'rgba(128,0,0,0.02)' : 'transparent' }} onClick={() => toggleBatchView(batchId)}>
                                            <td style={{ fontWeight: 800, color: 'var(--primary)' }}>{batchId}</td>
                                            <td>{count} candidates</td>
                                            <td>
                                                <span style={{ padding: '4px 12px', borderRadius: '20px', background: '#dcfce7', color: '#166534', fontSize: '12px', fontWeight: 700 }}>
                                                    IN QUEUE
                                                </span>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>
                                                <button 
                                                    style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', marginLeft: 'auto' }}
                                                >
                                                    {expandedBatchId === batchId ? 'Hide' : 'View Details'}
                                                    <Icon icon={expandedBatchId === batchId ? "ion:chevron-up" : "ion:chevron-down"} />
                                                </button>
                                            </td>
                                        </tr>
                                        {expandedBatchId === batchId && (
                                            <tr>
                                                <td colSpan="4" style={{ padding: '0 24px 24px' }}>
                                                    <div style={{ 
                                                        background: 'white', 
                                                        border: '1px solid var(--border-light)', 
                                                        borderRadius: '12px', 
                                                        padding: '16px',
                                                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.02)'
                                                    }}>
                                                        {fetchingBatch ? (
                                                            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)' }}>
                                                                <LoadingSpinner message="Fetching candidates..." />
                                                            </div>
                                                        ) : batchCandidates.length === 0 ? (
                                                            <div style={{ textAlign: 'center', padding: '20px', color: 'var(--text-muted)', fontSize: '13px', fontWeight: 600 }}>
                                                                No candidates from this batch are currently ready in the TRACKER stage.
                                                            </div>
                                                        ) : (
                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' }}>

                                                                {batchCandidates.map(c => (
                                                                    <div key={c.id} style={{ 
                                                                        padding: '12px', 
                                                                        background: '#f8fafc', 
                                                                        borderRadius: '10px', 
                                                                        border: '1px solid #e2e8f0',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '12px'
                                                                    }}>
                                                                        <div style={{ 
                                                                            width: '32px', 
                                                                            height: '32px', 
                                                                            background: 'var(--primary)', 
                                                                            color: 'white', 
                                                                            borderRadius: '8px',
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            fontWeight: 900,
                                                                            fontSize: '12px'
                                                                        }}>
                                                                            {c.chest_number}
                                                                        </div>
                                                                        <div style={{ flex: 1 }}>
                                                                            <div style={{ fontSize: '12px', fontWeight: 800 }}>{c.full_name}</div>
                                                                            <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{c.status} | {c.laps_completed} Laps</div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default BatchStats;
